/* ============================================
   ZONE RESOLVER — (x,y) -> zone 18-zones + flank
   ============================================
   Convertit les coordonnees du ballon en zone
   de la grille 18-zones definie dans la spec.

   x: 0 = but propre, 1 = but adverse
   y: 0 = haut, 1 = bas

   Utilise les bornes de ZONES (catalog-data.js)
   pour determiner la zone exacte.
   ============================================ */

import { ZONES, ZONE_IDS } from '../catalog/catalog-data.js'

/**
 * Resolve (x, y) ball coordinates to a zone ID and flank.
 *
 * @param {number} x - Ball x position (0 = own goal, 1 = opponent goal)
 * @param {number} y - Ball y position (0 = top, 1 = bottom)
 * @returns {{ zone: string, flank: string|null }}
 */
export function resolveZone(x, y) {
  // Clamp to [0, 1]
  x = Math.max(0, Math.min(1, x))
  y = Math.max(0, Math.min(1, y))

  // Determine flank from y position
  // y < 0.35 -> "left" (couloir haut)
  // y > 0.65 -> "right" (couloir bas)
  // otherwise -> null (axe central)
  const flank = (y < 0.35) ? 'left' : (y > 0.65) ? 'right' : null

  // Try to find the best matching zone based on the ZONES definitions.
  // For flanked zones (A1-A6, E1-E3), they only match when on a flank.
  // For central zones (B, C1, C2, D1, D2, F1, F2, F3, G), they match when not on flank.
  // Special cases: D1, D2, C1, C2 can match regardless of flank (goal areas).

  let bestZone = 'F2' // fallback: center of the pitch

  // Priority order: try specific zones first, then broader ones.
  // We iterate ZONE_IDS and find the zone whose x/y bounds contain the point.

  for (const id of ZONE_IDS) {
    const z = ZONES[id]
    const [xMin, xMax] = z.x
    const [yMin, yMax] = z.y

    if (x >= xMin && x < xMax) {
      if (z.flanks) {
        // Flanked zone — matches top band (y < yMax of top) or bottom band (y > 1-yMax)
        // The ZONES define y range for the TOP flank. Bottom flank is mirrored.
        if (y >= yMin && y < yMax) {
          // Top flank
          bestZone = id
          break
        }
        if (y > (1 - yMax) && y <= (1 - yMin)) {
          // Bottom flank (mirror)
          bestZone = id
          break
        }
      } else {
        // Central zone — check y bounds directly
        if (y >= yMin && y < yMax) {
          bestZone = id
          break
        }
      }
    }
  }

  // Flank is only relevant for flanked zones
  const zoneInfo = ZONES[bestZone]
  const resolvedFlank = zoneInfo && zoneInfo.flanks ? flank : null

  return { zone: bestZone, flank: resolvedFlank }
}

/**
 * Map a flank value to a human-readable side name.
 *
 * @param {string|null} flank - 'left', 'right', or null
 * @param {string} lang - Language code ('fr' or 'en')
 * @returns {string} Side name or empty string
 */
export function flankToSide(flank, lang = 'fr') {
  if (!flank) return ''
  if (lang === 'fr') {
    return flank === 'left' ? 'gauche' : 'droit'
  }
  return flank === 'left' ? 'left' : 'right'
}

/**
 * Map a zone ID to a human-readable zone name (for {zone} variable).
 *
 * @param {string} zoneId - Zone ID (e.g. 'F3', 'G', 'C2')
 * @param {string} lang - Language code
 * @returns {string} Zone name in natural language
 */
export function zoneToLabel(zoneId, lang = 'fr') {
  if (lang === 'fr') {
    const labels = {
      D1: 'les six metres',
      C1: 'la surface',
      B: 'la relance',
      A1: 'le couloir',
      A2: 'le couloir',
      A3: 'le couloir',
      A4: 'le couloir',
      A5: 'le couloir',
      A6: 'le couloir',
      E1: 'le demi-espace',
      E2: 'le demi-espace',
      E3: 'le demi-espace',
      F1: 'le rond central',
      F2: 'le milieu de terrain',
      F3: 'les trente metres',
      G: 'la zone de frappe',
      C2: 'la surface adverse',
      D2: 'les six metres adverses',
    }
    return labels[zoneId] || zoneId
  }
  // English fallback
  return ZONES[zoneId]?.label || zoneId
}
